/**
 * useAccountSwitcher — Multi-Account Hook (2026-07-26)
 * ====================================================
 *
 * WhatsApp/Telegram-style account switcher. Consumes the low-level
 * `lib/multiAccountStore.js` and the existing token bridge / query
 * client to safely rotate sessions on the same device.
 *
 * Public API returned by the hook:
 *   accounts             — Account[]        (all stored accounts)
 *   activeUserId         — string | null    (currently active user id)
 *   isSwitching          — boolean          (in-flight guard)
 *   refresh()            — Promise<void>    (re-read from IDB)
 *   addAccount()         — starts a NEW login flow WITHOUT logging
 *                          out the current user. Snapshots the active
 *                          session first, then clears the runtime
 *                          bearer + query cache so <AuthProvider>
 *                          drops the user and the app lands on /login.
 *   switchToAccount(uid) — swaps tokens with the stored ones for `uid`,
 *                          hard-clears the TanStack Query cache + IDB
 *                          persister to guarantee data isolation,
 *                          calls `checkAuth()` so the AuthProvider
 *                          rehydrates the target user.
 *   removeAccount(uid)   — logs out that account (revokes on the
 *                          server if it's the active one) and drops
 *                          it from the store.
 *
 * Data isolation is enforced by:
 *   1. `queryClient.clear()` on every switch (in-memory cache purge).
 *   2. `clearPersistedCache()` on every switch (IDB snapshot purge).
 *   3. Session hint reset so <AuthContext.checkAuth> re-runs against
 *      the new bearer.
 *
 * FCM notifications continue to be user-aware without any special
 * handling here — the AuthContext already re-registers the device
 * token via `useEffect([user])` and the backend `device_tokens.
 * register` endpoint upserts by `(user_id, device_id)`. The switch
 * flow triggers that effect automatically.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  listAccounts, getActiveUserId, setActiveUserId,
  upsertAccount, removeAccount as removeAccountFromStore,
  captureFromLoginResponse,
} from '../lib/multiAccountStore';
import { saveTokensFromResponse, clearTokens } from '../lib/tokenBridge';
import { purgeUserScopedCaches } from '../lib/sessionPurge';

const ACCESS_KEY  = 'mesp_access_token';
const REFRESH_KEY = 'mesp_refresh_token';

export function useAccountSwitcher() {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [activeUserId, setActiveId] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);

  const refresh = useCallback(async () => {
    const [list, aid] = await Promise.all([listAccounts(), getActiveUserId()]);
    setAccounts(list);
    setActiveId(aid);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Whenever the AuthContext's user changes (login/logout), snapshot
  // the active user into the multi-account store so the switcher
  // sees a fresh entry with the latest tokens on next mount.
  useEffect(() => {
    if (!user || user === false) return;
    (async () => {
      try {
        const access  = window.localStorage.getItem(ACCESS_KEY)  || null;
        const refreshT = window.localStorage.getItem(REFRESH_KEY) || null;
        const snap = captureFromLoginResponse(
          { access_token: access, refresh_token: refreshT },
          user,
        );
        if (!snap) return;
        await upsertAccount(snap);
        await setActiveUserId(snap.user_id);
        await refresh();
      } catch (_e) { /* IDB unavailable — ignore */ }
    })();
  }, [user, refresh]);

  /**
   * Start a NEW login flow without dropping the currently signed-in
   * user from the store. We:
   *   • Ensure the current session is snapshotted (the useEffect above
   *     already does this on any user change).
   *   • Clear the RUNTIME bearer tokens so <AuthProvider> drops the
   *     current user.
   *   • Purge the query cache so the login screen isn't rendered on
   *     top of a stale dashboard while the drop propagates.
   *   • Navigate to /login where the standard form takes over.
   */
  const addAccount = useCallback(async () => {
    // Belt-and-braces snapshot in case the user effect above never ran
    // (e.g. brand-new user who just registered on this device).
    if (user && user !== false) {
      const access  = window.localStorage.getItem(ACCESS_KEY)  || null;
      const refreshT = window.localStorage.getItem(REFRESH_KEY) || null;
      const snap = captureFromLoginResponse(
        { access_token: access, refresh_token: refreshT },
        user,
      );
      if (snap) await upsertAccount(snap);
    }
    // Drop runtime tokens + query cache so the app is a clean slate
    // for the incoming login.
    clearTokens();
    purgeUserScopedCaches();
    try { window.localStorage.removeItem('mesp_session_hint'); } catch (_e) { /* ignore */ }
    try { document.cookie = 'mesp_session_hint=; Max-Age=0; path=/'; } catch (_e) { /* ignore */ }
    try {
      const [{ clearPersistedCache }, { queryClient }] = await Promise.all([
        import('../lib/query/persister'),
        import('../lib/query/queryClient'),
      ]);
      queryClient.clear();
      await clearPersistedCache();
    } catch (_e) { /* ignore */ }
    setUser(false);
    navigate('/login', { replace: true, state: { multi_account_add: true } });
  }, [user, setUser, navigate]);

  /**
   * Swap the active session with the stored one for `userId`. If the
   * target account has no refresh_token (evicted / never captured)
   * we fall back to `addAccount()` so the user re-authenticates.
   */
  const switchToAccount = useCallback(async (userId) => {
    if (!userId) return { ok: false, reason: 'no_user_id' };
    if (isSwitching) return { ok: false, reason: 'in_flight' };
    if (userId === activeUserId) return { ok: true, reason: 'already_active' };
    setIsSwitching(true);
    try {
      const list = await listAccounts();
      const target = list.find((a) => a.user_id === String(userId));
      if (!target) return { ok: false, reason: 'not_found' };
      if (!target.refresh_token && !target.access_token) {
        // Tokens rotated out — treat as "add account" for this email.
        await addAccount();
        return { ok: false, reason: 'needs_relogin' };
      }
      // 1. Snapshot the current user so their tokens are fresh in IDB
      //    (they may have been refreshed silently since login).
      if (user && user !== false) {
        const curAccess  = window.localStorage.getItem(ACCESS_KEY)  || null;
        const curRefresh = window.localStorage.getItem(REFRESH_KEY) || null;
        const curSnap = captureFromLoginResponse(
          { access_token: curAccess, refresh_token: curRefresh },
          user,
        );
        if (curSnap) await upsertAccount(curSnap);
      }
      // 2. Hard-purge in-memory + persisted query caches so the target
      //    user never sees any of the previous user's data.
      try {
        const [{ clearPersistedCache }, { queryClient }] = await Promise.all([
          import('../lib/query/persister'),
          import('../lib/query/queryClient'),
        ]);
        queryClient.clear();
        await clearPersistedCache();
      } catch (_e) { /* ignore */ }
      // 3. Purge every user-scoped browser cache (dashboard stats,
      //    teacher KPIs, chat snapshots, recent items) — hard
      //    isolation between accounts.
      purgeUserScopedCaches();
      // 4. Rotate BOTH transports to the target account. The explicit
      //    body refresh_token beats the previous account's cookie
      //    (backend "explicit beats ambient" rule), so the server
      //    re-issues HttpOnly cookies FOR THE TARGET and returns a
      //    fresh access/refresh pair for the Bearer bridge.
      // 4a. Preventative (support team rev 2) — explicitly kill the
      //     CURRENT server session + HttpOnly cookies BEFORE minting the
      //     target account's session, so no residue of the old identity
      //     survives the swap. Best-effort: an already-dead session 401s
      //     harmlessly.
      try {
        const axiosMod = (await import('axios')).default;
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await axiosMod.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      } catch (_e) { /* session already dead — ignore */ }
      clearTokens();
      let fresh = null;
      try {
        const axios = (await import('axios')).default;
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const r = await axios.post(
          `${API_URL}/api/auth/refresh`,
          { refresh_token: target.refresh_token },
          { withCredentials: true },
        );
        fresh = r.data;
      } catch (_e) { /* rejected / expired — force re-login below */ }
      if (!fresh?.access_token) {
        await addAccount();
        return { ok: false, reason: 'needs_relogin' };
      }
      saveTokensFromResponse(fresh);
      await upsertAccount({
        user_id: String(userId),
        access_token: fresh.access_token,
        refresh_token: fresh.refresh_token || target.refresh_token,
      });
      try { window.localStorage.setItem('mesp_session_hint', '1'); } catch (_e) { /* ignore */ }
      // 5. Set the active pointer + re-hydrate via AuthContext.
      await setActiveUserId(String(userId));
      await checkAuth();
      // v1.1.0 fix #1 (rev 2 — support team) — deterministically re-bind
      // this device's FCM token to the NEW active user. The freshly
      // issued access token is passed EXPLICITLY (runs after
      // saveTokensFromResponse) so the upload never races a stale
      // localStorage read. Backend revokes the previous owner's binding.
      try {
        const bridge = await import('../lib/mobileBridge');
        if (bridge?.registerPushToken) {
          await bridge.registerPushToken({ force: true, accessToken: fresh.access_token });
        }
      } catch (_e) { /* web / bridge absent — ignore */ }
      await refresh();
      // 6. Land on the dashboard so the switch feels instant.
      navigate('/dashboard', { replace: true });
      return { ok: true };
    } finally {
      setIsSwitching(false);
    }
  }, [activeUserId, isSwitching, user, addAccount, checkAuth, refresh, navigate]);

  /**
   * Log out a specific stored account. If it's the currently active
   * one, we do a real /auth/logout so the server-side session dies;
   * otherwise we just drop the local snapshot (server retains the
   * still-valid refresh token, which will simply expire on schedule).
   */
  const removeAccountFn = useCallback(async (userId) => {
    if (!userId) return;
    const uid = String(userId);
    if (uid === activeUserId) {
      // v1.1.0 fix #1 — unbind FCM before the session dies (needs auth).
      try {
        const bridge = await import('../lib/mobileBridge');
        if (bridge?.revokePushToken) await bridge.revokePushToken();
      } catch (_e) { /* ignore */ }
      // Hard logout of the current session.
      try {
        const axios = (await import('axios')).default;
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      } catch (_e) { /* server unreachable — proceed anyway */ }
      clearTokens();
      purgeUserScopedCaches();
      try { window.localStorage.removeItem('mesp_session_hint'); } catch (_e) { /* ignore */ }
      try {
        const [{ clearPersistedCache }, { queryClient }] = await Promise.all([
          import('../lib/query/persister'),
          import('../lib/query/queryClient'),
        ]);
        queryClient.clear();
        await clearPersistedCache();
      } catch (_e) { /* ignore */ }
      await removeAccountFromStore(uid);
      await refresh();
      setUser(false);
      // Sep 2026 — Force Logout (auth-loop fix). Explicit sign-out must
      // NEVER auto-hop into another stored account (that was the loop:
      // logout → silent switch → navigate('/dashboard')). Wipe ALL
      // stored accounts + browser state and land on a clean /login.
      try {
        const { clearAllAccounts } = await import('../lib/multiAccountStore');
        await clearAllAccounts();
      } catch (_e) { /* ignore */ }
      try {
        const { hardWipeBrowserState } = await import('../lib/sessionPurge');
        hardWipeBrowserState();
      } catch (_e) { /* ignore */ }
      await refresh();
      navigate('/login', { replace: true });
      return;
    }
    // Non-active — just prune the IDB entry.
    await removeAccountFromStore(uid);
    await refresh();
  }, [activeUserId, refresh, setUser, navigate, switchToAccount]);

  return {
    accounts,
    activeUserId,
    isSwitching,
    refresh,
    addAccount,
    switchToAccount,
    removeAccount: removeAccountFn,
  };
}

export default useAccountSwitcher;
